/**
 * The `ContentItem` component is responsible for rendering and managing the display of
 * data on each card displayed on the landing page. It serves as a container for
 * UI elements and controls related to the presentation of content on the cards.
 * props.Item is the data item in the selected array which is supposed to be displayed.
 */

import { useEffect, useState } from "react";
import X_Circle from "../../../Assets/Icons/x-circle.svg";
import {
  TextField,
  InputAdornment,
  IconButton,
  useMediaQuery,
} from "@mui/material";
import dayjs from "dayjs";
import { Link, useNavigate } from "react-router-dom";
import Image from "./ADS/Component/ADSContent/Component/ADSContentRight/Component/ADSPlayground/Component/ADSPlaygroundContent/Component/FaceSwap/Image";
import Thumbnail from "../../../Assets/Images/Thumbnail-desktop.svg";
import axiosInstance from "../../../axiosInstance";
import FailModal from "../../../Components/FailModal";

const ContentItem = (props) => {
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery("(max-width: 960px)"); // Detect if the screen is mobile

  const [IsLiked, setIsLiked] = useState();
  const [ContextMenuIsOpen, setContextMenuIsOpen] = useState(false);
  const [DeleteIsOpen, setDeleteIsOpen] = useState(false);
  const [EditIsOpen, setEditIsOpen] = useState(false);
  const [ItemName, setItemName] = useState(props.Item.name);
  const [EditError, setEditError] = useState({
    hasError: false,
    errorText: "",
  });

  useEffect(() => {
    setIsLiked(props.Item.favorite);
    setItemName(props.Item.name);
  }, [props.Item]);

  const toggleLike = () => {
    let Req = {
      method: "PATCH",
      baseURL:
        window.localStorage.getItem("BaseURL") +
        "/services/projectmanagement/api/projects/" +
        props.Item.id,
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
      data: {
        id: props.Item.id,
        favorite: IsLiked ? false : true,
      },
    };
    axiosInstance(Req)
      .then((Res) => {
        console.log(Res);

        if (!props.LeftTabSelected) {
          props.RefreshItems();
        }

        if (IsLiked) {
          setIsLiked(false);
        } else {
          setIsLiked(true);
        }
      })
      .catch((Err) => {
        console.log(Err);
      });
  };

  const toggleDelete = () => {
    setContextMenuIsOpen(false);
    setDeleteIsOpen(true);
  };

  const toggleEdit = () => {
    setContextMenuIsOpen(false);
    setEditIsOpen(true);
  };

  const handleNameChange = (event) => {
    setItemName(event.target.value);
    const reachedMaxLimit = event.target.value?.length > 50;
    setEditError({
      hasError: reachedMaxLimit,
      errorText: reachedMaxLimit
        ? "The title you entered exceeds the maximum allowed length. Please ensure it is 50 characters or less."
        : "",
    });
  };

  const handleClear = () => {
    setItemName("");
  };

  const formatDate = (ISODate) => {
    // Parse ISO format date using Day.js
    const date = dayjs(ISODate);

    // Format the date
    const formattedDate = date.format("DD-MMM-YYYY");

    return formattedDate;
  };

  const requestEdit = () => {
    let Req = {
      method: "PATCH",
      baseURL:
        window.localStorage.getItem("BaseURL") +
        "/services/projectmanagement/api/projects/" +
        props.Item.id,
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
      data: {
        id: props.Item.id,
        name: ItemName,
      },
    };
    axiosInstance(Req)
      .then((Res) => {
        console.log(Res);
        setEditIsOpen(false);
        props.RefreshItems();
      })
      .catch((Err) => {
        console.log(Err);
        setEditError({
          hasError: true,
          errorText: "Wedding with the same title exists",
        });
      });
  };

  const acceptDelete = () => {
    let Req = {
      method: "DELETE",
      baseURL:
        window.localStorage.getItem("BaseURL") +
        "/services/projectmanagement/api/projects/" +
        props.Item.id,
      headers: {
        Authorization: "Bearer " + window.localStorage.getItem("Token"),
      },
    };
    axiosInstance(Req)
      .then(() => {
        props.RefreshItems();
        setDeleteIsOpen(false);
      })
      .catch((Err) => {
        console.log(Err);
      });
  };

  const shortenName = () => {
    let Name = props.Item.name;
    let CharLimit = 20;
    if (props.IsGridView || isMobileOrTablet) {
      CharLimit = 15;
    }

    if (Name.length < CharLimit) {
      return Name;
    } else {
      return (
        <>
          <span className="short-name">
            <p className="info-popup">{Name}</p>
            {Name.slice(0, CharLimit) + " ..."}
          </span>
        </>
      );
    }
  };

  // sets the localStorage variables needed for open ai design studio
  const saveWeddingInfo = () => {
    window.localStorage.setItem("CreatedWeddingName", props.Item.name);
    window.localStorage.setItem("CreatedWeddingId", props.Item.id);
  };

  const createChips = () => {
    return (
      <>
        {!!props?.Item?.imageCount && (
          <span className={`chip card square`}>
            {props?.Item?.imageCount} Images
          </span>
        )}
      </>
    );
  };

  const handleThumbnail = () => {
    if (props.Item.projectThumbnail?.[0]) {
      return (
        <Image
          ImageURL={
            window.localStorage.getItem("BaseURL") +
            props.Item.projectThumbnail?.[0]
          }
          className="item-image card"
        />
      );
    } else {
      return (
        <img alt="" src={Thumbnail} className="item-image card thumbnail" />
      );
    }
  };

  return (
    <div className="content-item card">
      <FailModal
        reverse
        show={DeleteIsOpen}
        title="Confirm Gallery Deletion"
        description="Are you sure you want to delete this gallery, including all AI-generated content, from the library?"
        onCancel={() => setDeleteIsOpen(false)}
        onRetry={acceptDelete}
        retryText="Delete"
      />
      {EditIsOpen ? (
        <>
          <div className="overlay" onClick={() => setEditIsOpen(false)}></div>
          <div className="modal">
            <h1>{"Rename " + props.Item.name}</h1>
            <TextField
              autoComplete="off" // Disable autofill
              fullWidth
              id="name"
              name="name"
              label={isMobileOrTablet ? "" : "Name"}
              placeholder={isMobileOrTablet ? "Name" : ""}
              variant="outlined"
              value={ItemName}
              onChange={handleNameChange}
              error={EditError.hasError}
              helperText={EditError.errorText}
              margin="normal"
              sx={{
                "& .MuiInputLabel-root.Mui-focused": {
                  color: "#171717",
                },
                "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                  {
                    borderColor: isMobileOrTablet ? "transparent" : "#79747E", // Change border color when focused
                  },
                "& .MuiInputBase-root": {
                  borderRadius: "8px",
                  height: "50px", // Set the height of the input field to 50px
                },
                backgroundColor: isMobileOrTablet ? "#F3EEE6" : "transparent",
                borderRadius: isMobileOrTablet ? "4px 4px 0 0" : "4px",
                borderBottom: isMobileOrTablet ? "3px solid #FB4764" : "none",
                ...(isMobileOrTablet && {
                  "& .MuiOutlinedInput-root .MuiOutlinedInput-notchedOutline": {
                    borderColor: "transparent",
                  },
                }),
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {props.Item.name && ( // Render clear button only if name has value
                      <IconButton
                        aria-label="clear name"
                        onClick={handleClear}
                        edge="end"
                        style={{ color: "#FB4764" }}
                      >
                        <img alt="x" src={X_Circle} />
                      </IconButton>
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <section>
              <span onClick={() => setEditIsOpen(false)}>Cancel</span>
              <span
                className={
                  ItemName.length === 0 || EditError.hasError
                    ? "disabled-action"
                    : ""
                }
                onClick={() => requestEdit()}
              >
                Save
              </span>
            </section>
          </div>
        </>
      ) : null}
      {ContextMenuIsOpen ? (
        <>
          <div
            className="overlay-no-blurr"
            onClick={() => setContextMenuIsOpen(false)}
          ></div>
          <div
            className={
              isMobileOrTablet && props.IsGridView
                ? "context-menu context-menu-long"
                : "context-menu"
            }
          >
            <section className="context-menu-row" onClick={() => toggleEdit()}>
              Rename
            </section>
            <section
              className="context-menu-row"
              onClick={() => toggleDelete()}
            >
              Delete
            </section>
            {props.IsGridView ? (
              <Link
                to={`/Home/AI_Studio/` + props.Item.id}
                className="context-menu-row"
                onClick={() => saveWeddingInfo()}
              >
                Open
              </Link>
            ) : null}
          </div>
        </>
      ) : null}
      <section className="actions">
        <span
          className={IsLiked ? "like-btn liked" : "like-btn"}
          onClick={() => toggleLike(IsLiked)}
        ></span>
        <span
          className="context-menu-trigger"
          onClick={() => setContextMenuIsOpen(true)}
        ></span>
      </section>
      {props.IsGridView ? (
        <div style={{ display: "flex", alignItems: "center", gap: "36px" }}>
          <div
            onClick={() => {
              saveWeddingInfo();
              navigate(`/Home/AI_Studio/` + props.Item.id);
            }}
            style={{
              marginBottom: props.IsGridView ? "0" : "18px",
              cursor: "pointer",
            }}
          >
            {handleThumbnail()}
          </div>
          <section className="item-details">
            <Link
              to={`/Home/AI_Studio/` + props.Item.id}
              onClick={() => saveWeddingInfo()}
            >
              <p className="item-name">{shortenName()}</p>
            </Link>
            <p className="item-date">
              {"Created on " + formatDate(props.Item.createdDate)}
            </p>
          </section>
        </div>
      ) : (
        <>
          <div
            onClick={() => {
              saveWeddingInfo();
              navigate(`/Home/AI_Studio/` + props.Item.id);
            }}
            style={{
              marginBottom: props.IsGridView ? "0" : "18px",
              cursor: "pointer",
            }}
          >
            {handleThumbnail()}
          </div>
          <section className="item-details">
            <Link
              to={`/Home/AI_Studio/` + props.Item.id}
              onClick={() => saveWeddingInfo()}
            >
              <p className="item-name">{shortenName()}</p>
            </Link>
            <p className="item-date">
              {"Created on " + formatDate(props.Item.createdDate)}
            </p>
          </section>
        </>
      )}
      {!props.IsGridView && (
        <Link
          to={`/Home/AI_Studio/` + props.Item.id}
          onClick={() => saveWeddingInfo()}
        >
          <p className="open-trigger">Open</p>
        </Link>
      )}
      <section className="chips-wrapper card">{createChips()}</section>
    </div>
  );
};

export default ContentItem;
